import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Container, Row, Col, ProgressBar } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { register } from '../../services/auth';
import { useAuth } from '../../contexts/AuthContext';
import LocationSelector from '../common/LocationSelector';

const Register = () => {
  const navigate = useNavigate();
  const { isAuthenticated, refreshUser } = useAuth();
  const [registerError, setRegisterError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // Redirect if user is already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Calculate the maximum date for 18+ users
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 18);
  const maxDateString = maxDate.toISOString().split('T')[0];

  // Generate arrays for birthday dropdowns
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const days = Array.from({ length: 31 }, (_, i) => {
    const day = i + 1;
    return { value: day.toString().padStart(2, '0'), label: day.toString() };
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 82 }, (_, i) => {
    const year = currentYear - 18 - i;
    return { value: year.toString(), label: year.toString() };
  });

  // Step 1 validation schema
  const step1Schema = Yup.object({
    username: Yup.string()
      .min(3, 'Username must be at least 3 characters')
      .max(20, 'Username must be less than 20 characters')
      .matches(/^[a-zA-Z0-9]+$/, 'Username can only contain letters and numbers')
      .required('Username is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('Password is required')
  });

  // Step 2 validation schema
  const step2Schema = Yup.object({
    birthday: Yup.date()
      .max(maxDateString, 'You must be at least 18 years old')
      .required('Birthday is required'),
    sex: Yup.string()
      .required('Gender is required'),
    orientation: Yup.string()
      .required('Orientation is required'),
    looking_to: Yup.string()
      .required('Looking to is required')
  });

  // Step 3 validation schema
  const step3Schema = Yup.object({
    city: Yup.string()
      .required('Location is required'),
    terms: Yup.boolean()
      .oneOf([true], 'You must accept the terms and conditions')
  });

  // Full validation schema for submission
  const validationSchema = Yup.object({
    username: Yup.string()
      .min(3, 'Username must be at least 3 characters')
      .max(20, 'Username must be less than 20 characters')
      .matches(/^[a-zA-Z0-9]+$/, 'Username can only contain letters and numbers')
      .required('Username is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('Password is required'),
    birthday: Yup.date()
      .max(maxDateString, 'You must be at least 18 years old')
      .required('Birthday is required'),
    sex: Yup.number()
      .required('Gender is required'),
    orientation: Yup.number()
      .required('Orientation is required'),
    looking_to: Yup.number()
      .required('Looking to is required'),
    city: Yup.string()
      .required('Location is required'),
    terms: Yup.boolean()
      .oneOf([true], 'You must accept the terms and conditions')
  });

  // Initial form values
  const initialValues = {
    username: '',
    email: '',
    password: '',
    birthday: '',
    birthMonth: '',
    birthDay: '',
    birthYear: '',
    sex: '',
    orientation: '',
    looking_to: '',
    city: '',
    region: '',
    country: '',
    latitude: '0',
    longitude: '0',
    ip: '127.0.0.1',
    gclid: '',
    utm_source: '',
    utm_campaign: '',
    utm_medium: '',
    utm_term: '',
    utm_content: '',
    clickid: '',
    g_score: '',
    terms: false
  };

  // Helper function to update birthday from dropdowns
  const updateBirthday = (month, day, year, setFieldValue) => {
    if (month && day && year) {
      const birthday = `${year}-${month}-${day}`;
      setFieldValue('birthday', birthday);
    }
  };

  // Validate current step
  const validateStep = async (values, step) => {
    try {
      if (step === 1) {
        await step1Schema.validate(values, { abortEarly: false });
      } else if (step === 2) {
        await step2Schema.validate(values, { abortEarly: false });
      } else if (step === 3) {
        await step3Schema.validate(values, { abortEarly: false });
      }
      return {};
    } catch (err) {
      const errors = {};
      err.inner.forEach((e) => {
        errors[e.path] = e.message;
      });
      return errors;
    }
  };

  // Handle next step
  const handleNextStep = async (values, setTouched, setErrors) => {
    const errors = await validateStep(values, currentStep);
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
      setRegisterError('');
    } else {
      setErrors(errors);
      // Mark fields as touched to show errors
      const touchedFields = {};
      Object.keys(errors).forEach(key => {
        touchedFields[key] = true;
      });
      setTouched(touchedFields);
    }
  };

  // Handle previous step
  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
    setRegisterError('');
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setRegisterError('');
    try {
      console.log('Submitting registration with location data:', {
        city: values.city,
        region: values.region,
        country: values.country,
        latitude: values.latitude,
        longitude: values.longitude
      });

      const response = await register(values);
      console.log('Registration successful:', response);

      // The backend already creates a session cookie on registration
      // Just refresh the user data to update the auth context
      await refreshUser();

      // Navigate to homepage
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Registration error:', error);
      if (error.errors) {
        Object.keys(error.errors).forEach(key => {
          setFieldError(key, error.errors[key][0]);
        });
        setRegisterError('Please correct the errors in the form.');
      } else {
        setRegisterError('Registration failed. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Step titles
  const stepTitles = {
    1: 'Account Details',
    2: 'About You',
    3: 'Location & Terms'
  };

  return (
    <Container>
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow-sm border-0 my-5">
            <Card.Body className="p-4">
              {/* Progress indicator */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  {[1, 2, 3].map((step) => (
                    <div
                      key={step}
                      className={`d-flex align-items-center justify-content-center rounded-circle ${
                        step === currentStep
                          ? 'bg-primary text-white'
                          : step < currentStep
                          ? 'bg-success text-white'
                          : 'bg-light text-muted'
                      }`}
                      style={{ width: '40px', height: '40px', fontWeight: 'bold' }}
                    >
                      {step < currentStep ? <i className="bi bi-check"></i> : step}
                    </div>
                  ))}
                </div>
                <ProgressBar now={(currentStep / totalSteps) * 100} className="mb-2" style={{ height: '4px' }} />
              </div>

              <div className="text-center mb-4">
                <h2 className="fw-bold">{stepTitles[currentStep]}</h2>
                <p className="text-muted">Step {currentStep} of {totalSteps}</p>
              </div>

              {registerError && (
                <Alert variant="danger" className="mb-4">
                  {registerError}
                </Alert>
              )}

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue,
                  setTouched,
                  setErrors
                }) => (
                  <Form onSubmit={handleSubmit}>
                    {/* Step 1: Account Details */}
                    {currentStep === 1 && (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>Username</Form.Label>
                          <Form.Control
                            type="text"
                            name="username"
                            value={values.username}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.username && errors.username}
                            placeholder="Choose a username"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.username}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.email && errors.email}
                            placeholder="Enter your email"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-4">
                          <Form.Label>Password</Form.Label>
                          <Form.Control
                            type="password"
                            name="password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.password && errors.password}
                            placeholder="Create a password"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.password}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted">
                            Must be at least 8 characters long
                          </Form.Text>
                        </Form.Group>

                        <Button
                          variant="primary"
                          className="w-100 py-2"
                          onClick={() => handleNextStep(values, setTouched, setErrors)}
                        >
                          Continue
                        </Button>
                      </>
                    )}

                    {/* Step 2: About You */}
                    {currentStep === 2 && (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>Birthday</Form.Label>
                          <Row>
                            <Col xs={4}>
                              <Form.Select
                                name="birthMonth"
                                value={values.birthMonth}
                                onChange={(e) => {
                                  handleChange(e);
                                  updateBirthday(e.target.value, values.birthDay, values.birthYear, setFieldValue);
                                }}
                                onBlur={handleBlur}
                                isInvalid={touched.birthday && errors.birthday}
                              >
                                <option value="">Month</option>
                                {months.map((month) => (
                                  <option key={month.value} value={month.value}>
                                    {month.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>
                            <Col xs={4}>
                              <Form.Select
                                name="birthDay"
                                value={values.birthDay}
                                onChange={(e) => {
                                  handleChange(e);
                                  updateBirthday(values.birthMonth, e.target.value, values.birthYear, setFieldValue);
                                }}
                                onBlur={handleBlur}
                                isInvalid={touched.birthday && errors.birthday}
                              >
                                <option value="">Day</option>
                                {days.map((day) => (
                                  <option key={day.value} value={day.value}>
                                    {day.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>
                            <Col xs={4}>
                              <Form.Select
                                name="birthYear"
                                value={values.birthYear}
                                onChange={(e) => {
                                  handleChange(e);
                                  updateBirthday(values.birthMonth, values.birthDay, e.target.value, setFieldValue);
                                }}
                                onBlur={handleBlur}
                                isInvalid={touched.birthday && errors.birthday}
                              >
                                <option value="">Year</option>
                                {years.map((year) => (
                                  <option key={year.value} value={year.value}>
                                    {year.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>
                          </Row>
                          {touched.birthday && errors.birthday && (
                            <div className="text-danger small mt-1">{errors.birthday}</div>
                          )}
                          <Form.Text className="text-muted">
                            You must be at least 18 years old
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Gender</Form.Label>
                          <div>
                            <Form.Check
                              inline
                              type="radio"
                              id="sex-male"
                              name="sex"
                              label="Male"
                              value="1"
                              checked={values.sex === '1'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <Form.Check
                              inline
                              type="radio"
                              id="sex-female"
                              name="sex"
                              label="Female"
                              value="2"
                              checked={values.sex === '2'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                          {touched.sex && errors.sex && (
                            <div className="text-danger small mt-1">{errors.sex}</div>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Orientation</Form.Label>
                          <div>
                            <Form.Check
                              inline
                              type="radio"
                              id="orientation-straight"
                              name="orientation"
                              label="Straight"
                              value="1"
                              checked={values.orientation === '1'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <Form.Check
                              inline
                              type="radio"
                              id="orientation-gay"
                              name="orientation"
                              label="Gay"
                              value="2"
                              checked={values.orientation === '2'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <Form.Check
                              inline
                              type="radio"
                              id="orientation-bisexual"
                              name="orientation"
                              label="Bisexual"
                              value="3"
                              checked={values.orientation === '3'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                          {touched.orientation && errors.orientation && (
                            <div className="text-danger small mt-1">{errors.orientation}</div>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-4">
                          <Form.Label>Looking To</Form.Label>
                          <div>
                            <Form.Check
                              inline
                              type="radio"
                              id="looking_to-bid"
                              name="looking_to"
                              label="Bid on dates"
                              value="1"
                              checked={values.looking_to === '1'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <Form.Check
                              inline
                              type="radio"
                              id="looking_to-paid"
                              name="looking_to"
                              label="Get paid to date"
                              value="2"
                              checked={values.looking_to === '2'}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                          {touched.looking_to && errors.looking_to && (
                            <div className="text-danger small mt-1">{errors.looking_to}</div>
                          )}
                        </Form.Group>

                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-secondary"
                            className="w-50 py-2"
                            onClick={handlePrevStep}
                          >
                            Back
                          </Button>
                          <Button
                            variant="primary"
                            className="w-50 py-2"
                            onClick={() => handleNextStep(values, setTouched, setErrors)}
                          >
                            Continue
                          </Button>
                        </div>
                      </>
                    )}

                    {/* Step 3: Location & Terms */}
                    {currentStep === 3 && (
                      <>
                        <LocationSelector
                          onLocationSelect={(locationData) => {
                            if (locationData.city || locationData.region || locationData.country) {
                              setFieldValue('city', locationData.city);
                              setFieldValue('region', locationData.region);
                              setFieldValue('country', locationData.country);
                              setFieldValue('latitude', locationData.latitude.toString());
                              setFieldValue('longitude', locationData.longitude.toString());
                            }
                          }}
                          initialCity={values.city}
                          initialRegion={values.region}
                          initialCountry={values.country}
                          touched={touched}
                          errors={errors}
                        />

                        <Form.Group className="mb-4">
                          <Form.Check
                            type="checkbox"
                            id="terms"
                            name="terms"
                            label={
                              <span>
                                I agree to the{' '}
                                <Link to="/terms" target="_blank" className="text-decoration-none">
                                  Terms of Service
                                </Link>{' '}
                                and{' '}
                                <Link to="/privacy" target="_blank" className="text-decoration-none">
                                  Privacy Policy
                                </Link>
                              </span>
                            }
                            checked={values.terms}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.terms && errors.terms}
                          />
                          {touched.terms && errors.terms && (
                            <div className="text-danger small mt-1">{errors.terms}</div>
                          )}
                        </Form.Group>

                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-secondary"
                            className="w-50 py-2"
                            onClick={handlePrevStep}
                          >
                            Back
                          </Button>
                          <Button
                            variant="primary"
                            type="submit"
                            className="w-50 py-2"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Creating...' : 'Create Account'}
                          </Button>
                        </div>
                      </>
                    )}
                  </Form>
                )}
              </Formik>

              <div className="text-center mt-4">
                <p className="mb-0">
                  Already have an account?{' '}
                  <Link to="/login" className="text-decoration-none fw-bold">
                    Sign In
                  </Link>
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Register;
